/**
 * Session-renewal mechanics for `ApiClient`: what one `/auth/refresh` attempt
 * concludes, where a recovered — or definitively lost — session is published,
 * and the "retry once after a 401" helpers every request path in `client.ts`
 * shares. Split out of `client.ts` to stay under this repo's 500-LOC file
 * ceiling: `ApiClient` still owns identity (`doFetch`/`url`/`headers` are
 * INJECTED here, never rebuilt); this module owns only the recovery policy,
 * so the two can never drift.
 */
import type { ApiError, Result } from '@4f/shared';
import type { ZodType } from 'zod';
import { readEnvelope, safeFetch } from './envelope.ts';
import { SessionView } from './types.ts';

/** What one renewal attempt concluded. */
export type SessionRenewal = 'renewed' | 'expired' | 'unavailable';

/**
 * Where a recovered — or definitively lost — session lands. The client is what
 * sees the `401`, but the session store owns the token + user, so it is
 * NOTIFIED rather than reached into (DIP). Wired at the composition root.
 */
export interface SessionRecovery {
  /** A refreshed session was minted — adopt it (token + user) before the retry. */
  adopt(session: SessionView): void;
  /** The refresh cookie was rejected — the session is unrecoverable; drop it. */
  expire(): void;
}

/**
 * A running (or idle) single-flight `/auth/refresh` — built by
 * {@link createSessionRenewalCoordinator}.
 */
export interface SessionRenewalCoordinator {
  renew(): Promise<SessionRenewal>;
}

/**
 * Does `status` mean the refresh COOKIE was rejected — as opposed to "we could
 * not tell"? Only a dead cookie may cost the caller their session: every `5xx`
 * is the edge's problem, not the session's, and `408`/`429` both say "try
 * again". This is deliberately STRICTER than the edge's own `isTokenDead`
 * (`apps/api/src/routes/auth.ts` — anything under 500), because the edge only
 * reaches that predicate once the handler owns the request: a `429` from the
 * abuse limiter mounted in front of `/auth/*` short-circuits before it, leaving
 * the cookie intact, so treating one as a dead session here would log a user out
 * for having been rate-limited.
 */
function isSessionDead(status: number): boolean {
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

/**
 * How long a renewal may take before it is abandoned. Every other call in this
 * app is left to the browser's own network timeout; this one cannot be, because
 * it is SINGLE-FLIGHT: a refresh that hangs (a captive portal, a half-open
 * socket) stalls not just itself but the `401` recovery of every request in the
 * tab and the socket's reconnect ladder, all of which await this same promise.
 * Abandoning it reads as `unavailable`, which costs nobody their session and
 * lets the next `401` try again.
 */
const RENEWAL_TIMEOUT_MS = 10_000;

/**
 * Build the single-flight coordinator for `POST /auth/refresh`. Concurrent
 * `401`s across the surface (gallery + companions + billing all firing at once)
 * must collapse onto exactly ONE refresh: the edge's refresh rotation treats a
 * superseded token as REUSE and revokes the whole family, which would log the
 * user out for real. `postRefresh` is injected (DIP) — `ApiClient` owns the
 * request's shape (its `baseUrl` prefix, its headers) and applies the deadline
 * handed back to it; this module owns the single-flight and the outcome policy,
 * so the two can never drift.
 */
export function createSessionRenewalCoordinator(
  postRefresh: (signal: AbortSignal) => Promise<Response>,
  recovery: SessionRecovery | undefined,
): SessionRenewalCoordinator {
  let inFlight: Promise<SessionRenewal> | null = null;

  // Never routed through `requestJson`/`sendJson` — that would recurse into 401 recovery.
  async function perform(): Promise<SessionRenewal> {
    const fetched = await safeFetch(() => postRefresh(AbortSignal.timeout(RENEWAL_TIMEOUT_MS)));
    if (!fetched.ok) {
      // Transport never reached the edge (offline/DNS/aborted) — a blip must
      // never be mistaken for a dead session.
      return 'unavailable';
    }
    const response = fetched.value;
    const envelope = await readEnvelope(response, SessionView);
    if (envelope.ok) {
      recovery?.adopt(envelope.value);
      return 'renewed';
    }
    if (isSessionDead(response.status)) {
      recovery?.expire();
      return 'expired';
    }
    return 'unavailable';
  }

  return {
    renew(): Promise<SessionRenewal> {
      if (!inFlight) {
        // Clear the slot once this settles (either way) so a LATER 401 starts
        // a fresh refresh instead of replaying a stale outcome forever.
        inFlight = perform().finally(() => {
          inFlight = null;
        });
      }
      return inFlight;
    },
  };
}

/**
 * Issue one request; on a `401` — read off the raw `Response`, before any
 * envelope parsing hides the status — attempt exactly one recovery: renew the
 * session and, only when that renewed it, retry the identical request once.
 * `renew` is `undefined` when the caller opted out (`refresh`/`logout`/
 * `register`/`login` — see `client.ts`) or no {@link SessionRecovery} sink is
 * wired at all; either way the `401` passes through untouched, because with no
 * sink a retry would just re-send the same expired bearer. Shared by every
 * request path — JSON and SSE alike — so 401-handling can never drift between
 * them.
 */
export async function fetchWithRecovery(
  makeRequest: () => Promise<Response>,
  renew: (() => Promise<SessionRenewal>) | undefined,
): Promise<Result<Response, ApiError>> {
  const first = await safeFetch(makeRequest);
  if (!renew || !first.ok) {
    return first;
  }
  if (first.value.status !== 401) {
    return first;
  }
  const outcome = await renew();
  if (outcome !== 'renewed') {
    return first;
  }
  // Retrying discards this response; release its body rather than leak the stream.
  void first.value.body?.cancel();
  return safeFetch(makeRequest);
}

/**
 * {@link fetchWithRecovery} plus the envelope unwrap — the shared body of
 * `getJson`/`sendJson`/`sendForm`.
 */
export async function requestJson<T>(
  makeRequest: () => Promise<Response>,
  schema: ZodType<T>,
  renew: (() => Promise<SessionRenewal>) | undefined,
): Promise<Result<T, ApiError>> {
  const response = await fetchWithRecovery(makeRequest, renew);
  if (!response.ok) {
    return response;
  }
  return readEnvelope(response.value, schema);
}
